import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { serviceService } from "@/services";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { Ticket, AlarmClock, AlertTriangle, CheckCircle2, Star, Users, Loader2 } from "lucide-react";

const COLORS = ["#0d9488", "#06b6d4", "#f59e0b", "#8b5cf6", "#ec4899", "#64748b"];

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm animate-rise" data-testid={`svc-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value ?? "—"}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ text = "No data yet" }) {
  return <p className="text-sm text-muted-foreground py-10 text-center">{text}</p>;
}

export default function ServiceDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { serviceService.dashboard().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const byStatus = (data?.byStatus || []).filter((s) => s.value > 0);
  const byPriority = (data?.byPriority || []).filter((s) => s.value > 0);
  const workload = data?.workload || [];
  const comp = data?.slaCompliance || {};

  return (
    <div data-testid="service-dashboard-page">
      <PageHeader title="Service Dashboard" subtitle="Customer service health — tickets, SLA compliance and CSAT"
        actions={
          <button onClick={() => navigate("/admin/tickets")} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="svc-go-tickets">
            <Ticket className="h-4 w-4" /> View Tickets
          </button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-7">
        <Kpi label="Open" value={data?.open} icon={Ticket} color="text-sky-500" />
        <Kpi label="Breached" value={data?.breached} icon={AlertTriangle} color="text-red-500" />
        <Kpi label="Resolved" value={data?.resolved} icon={CheckCircle2} color="text-emerald-500" />
        <Kpi label="Total" value={data?.total} icon={AlarmClock} color="text-violet-500" />
        <Kpi label="Resp SLA %" value={comp.response != null ? `${comp.response}%` : "—"} icon={AlarmClock} color="text-teal-500" />
        <Kpi label="CSAT" value={data?.csatAvg != null ? `${data.csatAvg}★` : "—"} icon={Star} color="text-amber-500" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card title="Tickets by Status" testid="svc-by-status">
          {byStatus.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Tickets by Priority" testid="svc-by-priority">
          {byPriority.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byPriority}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#0d9488" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card title="Agent Workload (open tickets)" testid="svc-workload">
        <div className="flex items-center gap-1.5 mb-3 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Top agents by open ticket count</div>
        {workload.length === 0 ? <Empty text="No open tickets" /> : (
          <div className="space-y-2">
            {workload.map((w, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`svc-workload-${i}`}>
                <span className="w-40 truncate text-sm text-foreground">{w.name}</span>
                <div className="flex-1 bg-muted rounded-full h-2.5">
                  <div className="bg-primary h-2.5 rounded-full" style={{ width: `${Math.min(100, (w.value / (workload[0].value || 1)) * 100)}%` }} />
                </div>
                <span className="text-sm font-semibold text-foreground w-8 text-right">{w.value}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
